import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface FeaturedProduct {
  id: string;
  name: string;
  brand: string;
  price: number;
  rating: number;
  image: string;
}

export default function Hero() {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [featured, setFeatured] = useState<FeaturedProduct[]>([]);

  useEffect(() => {
    supabase
      .from('products')
      .select('id,name,brand,price,rating,image')
      .order('rating', { ascending: false })
      .limit(2)
      .then(({ data }) => {
        if (data) setFeatured(data);
      });
  }, []);

  const handleSearch = () => {
    if (search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <section className="pt-28 md:pt-36 pb-16 md:pb-20 border-b border-ink-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-[1fr_400px] gap-12 items-center">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-cta-600 tracking-wide">
            Independent since 2018. No retailer influence.
          </p>
          <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold leading-[1.05] tracking-tight text-ink-900">
            Find the best products<br className="hidden md:block" /> before you buy.
          </h1>
          <p className="mt-5 text-xl text-ink-500 max-w-xl leading-relaxed">
            Expert reviews, side-by-side comparisons, and buying guides. Tested by real people, not algorithms.
          </p>

          <div className="mt-8 max-w-xl">
            <div className="flex items-center bg-white border border-ink-200 rounded-xl h-14 shadow-soft focus-within:border-ink-400 transition">
              <i className="ri-search-line text-ink-400 text-base mx-4 flex-shrink-0"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search products, brands, or categories..."
                className="flex-1 h-full bg-transparent outline-none text-sm text-ink-800 placeholder:text-ink-400"
              />
              <button
                onClick={handleSearch}
                className="h-10 px-5 mx-2 rounded-lg bg-ink-900 hover:bg-ink-700 text-white text-sm font-semibold whitespace-nowrap transition flex-shrink-0"
              >
                Search
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-ink-400">
              <span>Trending:</span>
              {['Noise-cancelling headphones', 'Robot vacuums', 'RTX 5090'].map((t) => (
                <button
                  key={t}
                  onClick={() => navigate(`/search?q=${encodeURIComponent(t)}`)}
                  className="text-ink-600 hover:text-cta-600 transition underline underline-offset-2"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-12 flex items-center gap-8 flex-wrap">
            {[
              { n: '10,240+', l: 'Products reviewed' },
              { n: '842', l: 'Buying guides' },
              { n: '4.9/5', l: 'Reader trust score' },
            ].map((s) => (
              <div key={s.l}>
                <p className="font-display text-2xl font-bold text-ink-900">{s.n}</p>
                <p className="text-sm text-ink-500 mt-0.5">{s.l}</p>
              </div>
            ))}
            <div className="hidden sm:block h-8 w-px bg-ink-200"></div>
            <Link
              to="/editors-picks"
              className="text-sm font-semibold text-cta-600 hover:text-cta-700 transition flex items-center gap-1"
            >
              Editor's picks <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {featured.length > 0 && (
          <div className="hidden lg:block relative w-[400px] h-[460px]">
            <div className="absolute inset-8 bg-gradient-to-br from-cta-100 via-orange-50 to-brand-50 rounded-[3rem] rotate-3"></div>

            <Link
              to={`/product/${featured[0].id}`}
              className="group absolute top-2 right-4 w-56 bg-white rounded-2xl border border-ink-100 shadow-card p-3 rotate-3 hover:rotate-0 hover:-translate-y-1 transition duration-300 z-20"
            >
              <div className="aspect-square rounded-xl bg-ink-50 overflow-hidden mb-3">
                <img src={featured[0].image} alt={featured[0].name} className="w-full h-full object-contain p-3 group-hover:scale-105 transition duration-500" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">{featured[0].brand}</p>
              <p className="text-sm font-semibold text-ink-900 line-clamp-1 mt-0.5">{featured[0].name}</p>
              <div className="flex items-center gap-1 mt-1.5">
                <i className="ri-star-fill text-amber-500 text-xs"></i>
                <span className="text-xs font-semibold text-ink-800">{featured[0].rating}</span>
                <span className="ml-auto text-sm font-bold text-cta-600">${featured[0].price}</span>
              </div>
            </Link>

            {featured[1] && (
              <Link
                to={`/product/${featured[1].id}`}
                className="group absolute bottom-4 left-0 w-48 bg-white rounded-2xl border border-ink-100 shadow-card p-3 -rotate-3 hover:rotate-0 hover:-translate-y-1 transition duration-300 z-10"
              >
                <div className="aspect-square rounded-xl bg-ink-50 overflow-hidden mb-3">
                  <img src={featured[1].image} alt={featured[1].name} className="w-full h-full object-contain p-3 group-hover:scale-105 transition duration-500" />
                </div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-400">{featured[1].brand}</p>
                <p className="text-sm font-semibold text-ink-900 line-clamp-1 mt-0.5">{featured[1].name}</p>
                <div className="flex items-center gap-1 mt-1.5">
                  <i className="ri-star-fill text-amber-500 text-xs"></i>
                  <span className="text-xs font-semibold text-ink-800">{featured[1].rating}</span>
                  <span className="ml-auto text-sm font-bold text-cta-600">${featured[1].price}</span>
                </div>
              </Link>
            )}

            <div className="absolute top-1/2 -left-6 -translate-y-1/2 bg-white rounded-2xl shadow-card border border-ink-100 px-3.5 py-2.5 flex items-center gap-2 z-30">
              <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-fill text-sm"></i>
              </span>
              <div className="whitespace-nowrap">
                <p className="text-xs font-bold text-ink-900">Editor Verified</p>
                <p className="text-[10px] text-ink-400">Hands-on tested</p>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </section>
  );
}
